<script setup>
  import { ref, watch, computed, onMounted, nextTick } from 'vue'

  const props = defineProps({
    dataList: {
      type: Array,
      required: true,
      default: [],
    },
    tableList: {
      type: Array,
      required: true,
      default: [],
    },
    searchText: {
      type: String,
      required: true,
      default: '',
    },
    sortKey: {
      type: String,
      required: true,
      default: '',
    },
    sortDesc: {
      type: Boolean,
      required: true,
      default: false,
    },
    disableHover: {
      type: Boolean,
      default: false,
    },
    searchKey: {
      type: String,
      required: false,
      default: '',
    },
    searchSelect: {
      type: String,
      required: false,
      default: '',
    },
    searchList: {
      type: Object,
      required: false,
      default: [],
    },
    searchKey2: {
      type: String,
      required: false,
      default: '',
    },
    searchSelect2: {
      type: String,
      required: false,
      default: '',
    },
    searchList2: {
      type: Object,
      required: false,
      default: [],
    },
    searchColor: {
      type: String,
      required: false,
      default: '',
    },
  })

  const sortKey = ref(props.sortKey)
  const sortDesc = ref(props.sortDesc)
  const searchText = ref(props.searchText)
  const dataList = ref(props.dataList)
  const tableList = ref(props.tableList)
  const searchKey = ref(props.searchKey)
  const searchSelect = ref(props.searchSelect)
  const searchList = ref(props.searchList)
  const searchKey2 = ref(props.searchKey2)
  const searchSelect2 = ref(props.searchSelect2)
  const searchList2 = ref(props.searchList2)
  const searchColor = ref(props.searchColor)

  const emit = defineEmits(['length', 'select', 'numerator', 'row-action'])

  // watch更新処理 ///////////////////////////////////

  watch(
    () => props.dataList,
    newVal => {
      dataList.value = newVal
    }
  )

  watch(
    () => props.tableList,
    newVal => {
      tableList.value = newVal
    }
  )

  watch(
    () => props.searchText,
    newVal => {
      searchText.value = newVal
    }
  )

  watch(
    () => props.sortKey,
    newVal => {
      sortKey.value = newVal
    }
  )

  watch(
    () => props.sortDesc,
    newVal => {
      sortDesc.value = newVal
    }
  )

  watch(
    () => props.searchKey,
    newVal => {
      searchKey.value = newVal
    }
  )

  watch(
    () => props.searchSelect,
    newVal => {
      searchSelect.value = newVal
    }
  )

  watch(
    () => props.searchList,
    newVal => {
      searchList.value = newVal
    }
  )

  watch(
    () => props.searchKey2,
    newVal => {
      searchKey2.value = newVal
    }
  )

  watch(
    () => props.searchSelect2,
    newVal => {
      searchSelect2.value = newVal
    }
  )

  watch(
    () => props.searchList2,
    newVal => {
      searchList2.value = newVal
    }
  )

  watch(
    () => props.searchColor,
    newVal => {
      searchColor.value = newVal
    }
  )

  // テーブル表示用の計算 /////////////////////////////////

  const tableList2 = computed(() => {
    return tableList.value
      .map(item => {
        return {
          ...item,
          css: item.css ? item.css : '',
          format: item.format ? item.format : null,
          tag: item.tag ? item.tag : null,
          tag_css: item.tag_css ? item.tag_css : '',
          data: item.data ? item.data : (item, value) => value,
          hide: item.hide ? item.hide : false,
          html: item.html ? item.html : false,
        }
      })
      .filter(item => !item.hide)
  })

  function sort(key) {
    if (sortKey.value === key) {
      sortDesc.value = sortDesc.value === '' ? false : sortDesc.value === false ? true : ''
    } else {
      sortKey.value = key
      sortDesc.value = false
    }
  }

  function sortClass(key) {
    return {
      sortable: key !== '',
      asc: sortKey.value === key && sortDesc.value === false,
      desc: sortKey.value === key && sortDesc.value === true,
    }
  }

  const filtedData = computed(() => {
    if (!dataList.value || !tableList2.value) return []

    return (
      dataList.value
        .filter(item => {
          if (searchSelect.value !== '') {
            if (searchSelect.value !== item[searchKey.value]) {
              return false
            }
          }
          if (searchSelect2.value !== '') {
            if (searchSelect2.value !== tableList2.value.find(i => i.key === searchKey2.value).data(item, item[searchKey2.value])) {
              return false
            }
          }
          if (searchColor.value !== '') {
            // フラット構造のcolorInfoに対応
            if (!item.colorInfo) {
              return false
            }

            // colorInfoがオブジェクトの場合（電子申請用）
            if (typeof item.colorInfo === 'object') {
              // colorInfoの全ての値をチェック
              const colorValues = Object.values(item.colorInfo).filter(Boolean)
              return colorValues.some(color => color.includes(searchColor.value))
            }

            // 従来のcolorInfo.Value形式（他の画面用）
            if (item.colorInfo.Value) {
              return item.colorInfo.Value.indexOf(searchColor.value) !== -1
            }

            return false
          }
          const keywords = []
          for (const key in item) {
            const value = item[key]
            if (value === null || value === '' || (value && (value.Value === null || value.Value === ''))) {
              continue
            }
            if (tableList2.value.find(i => i.key === key)) {
              keywords.push(tableList2.value.find(i => i.key === key).data(item, value))
            }
          }

          if (searchText.value.trim() !== '') {
            const tags = searchText.value.split(' ')
            for (const tag of tags) {
              let isMatch = false
              for (const keyword of keywords) {
                if (keyword && keyword.toString().indexOf(tag) !== -1) {
                  isMatch = true
                  break
                }
              }
              if (!isMatch) {
                return false
              }
            }
          }
          return true
        })
        .sort((a, b) => {
          if (sortKey.value === '' || sortDesc.value === '') {
            return 0
          }
          const a2 = tableList2.value.find(i => i.key === sortKey.value).data(a, a[sortKey.value])
          const b2 = tableList2.value.find(i => i.key === sortKey.value).data(b, b[sortKey.value])
          if (sortDesc.value) {
            return a2 > b2 ? -1 : 1
          } else {
            return a2 > b2 ? 1 : -1
          }
        }) || []
    )
  })

  // length更新関数
  const updateLength = () => {
    if (!dataList.value) return

    const filteredLength = filtedData.value?.length || 0
    const totalLength = dataList.value?.length || 0

    // 0/0 の場合は空文字列を送信
    if (filteredLength === 0 && totalLength === 0) {
      emit('length', '')
      emit('numerator', 0)
      return
    }

    if (filteredLength >= 0 && totalLength >= 0) {
      emit('length', `${filteredLength} / ${totalLength}`)
      emit('numerator', filteredLength)
    }
  }

  // filtedDataが変更されたらlengthを更新
  watch(() => filtedData.value?.length, updateLength)

  // データリストが変更されたらlengthを更新（非同期処理に注意）
  watch(
    () => dataList.value,
    newVal => {
      if (newVal && newVal.length > 0) {
        // nextTickを使用して、computedプロパティが更新された後に実行
        nextTick(() => {
          updateLength()
        })
      }
    }
  )

  // コンポーネントマウント時にlengthを更新
  onMounted(() => {
    // 初期データがあれば更新
    nextTick(() => {
      if (dataList.value && dataList.value.length > 0) {
        updateLength()
      }
    })
  })

  const select = item => {
    emit('select', item)
  }

  // 修正された色取得関数
  const cellColor = (name, item) => {
    if (!item.colorInfo) {
      return ''
    }

    // フラット構造のcolorInfo（電子申請用）
    if (typeof item.colorInfo === 'object' && !item.colorInfo.Value) {
      return item.colorInfo[name] || ''
    }

    // 従来のcolorInfo.Value形式（他の画面用）
    if (item.colorInfo.Value) {
      let infos = item.colorInfo.Value.split(';').filter(Boolean)
      let className = ''
      for (let i = 0; i < infos.length; i++) {
        let [key, color] = infos[i].split(':')
        if (key === name) {
          className = color
          break
        }
      }
      return className
    }

    return ''
  }

  function view(table, item) {
    return table.format ? table.format(table.data(item, item[table.key])) : table.data(item, item[table.key])
  }

  function handleAction(table, item, event) {
    table.action(table, item, event)
    // 行アクションが実行されたことを親コンポーネントに通知
    emit('row-action', { table, item })
  }
</script>

<template>
  <div class="table-content" v-if="dataList">
    <table>
      <thead>
        <tr>
          <th v-for="item in tableList2" @click="item.sortable === false ? null : sort(item.key)" :class="sortClass(item.key)" :key="item.key">{{ item.label }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in filtedData" :key="index" @click="select(item)" :class="{ 'no-hover': disableHover }">
          <td v-for="table in tableList2" :class="[table.css, cellColor(table.key, item)]" :key="table.key">
            <template v-if="table.action && table.actionEnabled(table, item)">
              <span class="btn" @click="handleAction(table, item, $event)">{{ view(table, item) }}</span>
            </template>
            <span v-else-if="table.tag" class="tag" :class="[table.tag[table.data(item, item[table.key])], table.tag_css]">{{ view(table, item) }}</span>
            <template v-else>
              <div v-if="table.html" v-html="view(table, item)"></div>
              <template v-else>
                {{ view(table, item) }}
              </template>
            </template>
          </td>
          <td></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="less" scoped></style>
